import React, { useState, useEffect } from 'react';
import { Card, CardContent, Typography, Box, Button, Stack, FormControl, InputLabel, Select, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Grid, CircularProgress } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { toast } from 'react-toastify';
import PageHeader from '../../components/PageHeader';
import { formatCurrency } from '../../utils/formatters';
import { getTDSReport, exportTDSReport } from '../../api/endpoints/reports';
import { downloadBlob } from '../../utils/exportHelpers';

const TDSReport = () => {
  const [period, setPeriod] = useState('all');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReport();
  }, [period]);

  const fetchReport = async () => {
    try {
      setLoading(true);
      const params = {};
      if (period !== 'all') params.period = period;
      const res = await getTDSReport(params);
      setReport(res.data.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load TDS report');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      await downloadBlob(exportTDSReport({ period: period !== 'all' ? period : undefined }), `tds-report-${period || 'all'}.csv`);
      toast.success('TDS report exported');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to export report');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  const tdsData = report?.details || report?.data || [];

  return (
    <Box>
      <PageHeader title="TDS Report" subtitle="Tax Deducted at Source summary">
        <Button variant="outlined" startIcon={<FileDownloadIcon />} onClick={handleExport}>Export TDS Report</Button>
      </PageHeader>

      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Period</InputLabel>
          <Select value={period} label="Period" onChange={(e) => setPeriod(e.target.value)}>
            <MenuItem value="all">All Periods</MenuItem>
            <MenuItem value="Q1">Q1</MenuItem>
            <MenuItem value="Q2">Q2</MenuItem>
            <MenuItem value="Q3">Q3</MenuItem>
            <MenuItem value="Q4">Q4</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {report && (
        <>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={4}>
              <Card><CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ color: 'primary.main' }}>{formatCurrency(report.totalAmount || report.totalPayouts || 0)}</Typography>
                <Typography variant="caption" color="text.secondary">Total Payouts</Typography>
              </CardContent></Card>
            </Grid>
            <Grid item xs={4}>
              <Card><CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ color: 'secondary.main' }}>{formatCurrency(report.totalTds || 0)}</Typography>
                <Typography variant="caption" color="text.secondary">Total TDS Deducted</Typography>
              </CardContent></Card>
            </Grid>
            <Grid item xs={4}>
              <Card><CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h6">{report.tdsPercent || 10}%</Typography>
                <Typography variant="caption" color="text.secondary">TDS Rate</Typography>
              </CardContent></Card>
            </Grid>
          </Grid>

          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>TDS Details</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Astrologer</TableCell>
                      <TableCell align="right">Total Earnings</TableCell>
                      <TableCell align="right">TDS Rate</TableCell>
                      <TableCell align="right">TDS Deducted</TableCell>
                      <TableCell align="right">Net Paid</TableCell>
                      <TableCell align="right">Payouts</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tdsData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">No data found</TableCell>
                      </TableRow>
                    ) : tdsData.map((row, i) => (
                      <TableRow key={row._id || i} hover>
                        <TableCell><Typography fontWeight={500}>{row.astrologer?.name || row.name || row.astrologer || '-'}</Typography></TableCell>
                        <TableCell align="right">{formatCurrency(row.totalEarnings || row.amount || 0)}</TableCell>
                        <TableCell align="right">{row.tdsPercent || report?.tdsPercent || 10}%</TableCell>
                        <TableCell align="right">{formatCurrency(row.tdsDeducted || row.tds || 0)}</TableCell>
                        <TableCell align="right"><Typography fontWeight={600}>{formatCurrency((row.totalEarnings || row.amount || 0) - (row.tdsDeducted || row.tds || 0))}</Typography></TableCell>
                        <TableCell align="right">{row.transactions || row.count || 0}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
};

export default TDSReport;